import { useMemo, useState, type ReactNode } from "react";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { RequestError } from "@/app/queries";
import { useQueue, type QueueItem, type QueueMode } from "@/app/reviewQueries";
import { useMe } from "@/app/session";
import { Button } from "@/ui/Button";
import { EmptyState, NoAccess, SkeletonRows, StateCard } from "@/ui/states";
import { HintsCard, SelectedCard } from "./mentor/queue/Aside";
import { Buckets } from "./mentor/queue/Buckets";
import { Rows } from "./mentor/queue/Rows";
import { Segmented } from "./mentor/queue/Segmented";
import { SORTS, countLabel, filterRows, sortRows, type BucketId, type SortId } from "./mentor/queue/model";

const MODES: readonly { id: QueueMode; label: string }[] = [
  { id: "mine", label: "мои" },
  { id: "all", label: "все" },
  { id: "nocohort", label: "без когорты" },
];

const EMPTY_BODY: Record<QueueMode, string> = {
  mine: "Ни одна сдача ваших студентов не ждёт проверки. Новая появится здесь сразу после отправки — и придёт уведомление.",
  all: "Во всей школе нет сдач на проверке. Новые появятся здесь сразу после отправки студентом.",
  nocohort: "У студентов вне когорт нет сдач на проверке. Их работы встают в ту же очередь с тем же SLA.",
};

/** M1 «Очередь проверки»: корзины, сортировка, поиск, строки сдач и карточка
 *  выбранной сдачи. Режимы «мои / все / без когорты» — запрос к API, остальное
 *  считается на клиенте из строк. */
export function MentorQueuePage() {
  const me = useMe();
  const user = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const [mode, setMode] = useState<QueueMode>("mine");
  const queue = useQueue(mode);
  const frame = (children: ReactNode) => (
    <Shell nav user={user}>
      {children}
    </Shell>
  );

  const err = queue.error;
  if (err instanceof RequestError && err.status === 403)
    return frame(<NoAccess where="очередь проверки" body="Очередь проверки видят менторы и администраторы школы. Свои сдачи и вердикты вы найдёте в домашках." primary={{ label: "на мой маршрут", href: "/" }} />);
  if (err)
    return frame(
      <StateCard
        code="ERR_NETWORK"
        where="очередь проверки"
        tone="warn"
        title="Нет связи с сервером"
        body="Очередь не загрузилась. Сдачи и комментарии хранятся на сервере — ничего не потеряно."
        primary={{ label: "повторить сейчас", onClick: () => void queue.refetch() }}
      />,
    );
  if (!queue.data) return frame(<SkeletonRows />);
  return <Queue all={queue.data} mode={mode} onMode={setMode} user={user} />;
}

function Queue({ all, mode, onMode, user }: { all: QueueItem[]; mode: QueueMode; onMode: (m: QueueMode) => void; user?: { name: string; role: string } }) {
  const [bucket, setBucket] = useState<BucketId>("all");
  const [sort, setSort] = useState<SortId>("wait");
  const [query, setQuery] = useState("");
  const [selId, setSelId] = useState<string>();
  const rows = useMemo(() => sortRows(filterRows(all, bucket, query), sort), [all, bucket, query, sort]);
  // Выбор — по id; если выбранная строка ушла из фильтра, берём первую видимую.
  const selected = rows.find((r) => r.submission_id === selId) ?? rows[0];
  const reset = () => (setBucket("all"), setQuery(""));

  return (
    <Shell nav wide user={user}>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="min-w-0 pt-5">
          <div className="flex flex-wrap items-baseline gap-3.5 px-[22px]">
            <h1 className="m-0 font-mono text-[22px] font-bold tracking-[-.015em]">Очередь проверки</h1>
            <span className="font-mono text-[12px] text-mut" data-testid="count-label">
              {countLabel(all)}
            </span>
            <span className="flex-1" />
            <Segmented
              label="чьи сдачи"
              value={mode}
              options={MODES}
              onChange={(m) => {
                onMode(m);
                setSelId(undefined);
              }}
              testPrefix="mode"
            />
          </div>
          <div className="mt-4 px-[22px]">
            <Buckets
              all={all}
              value={bucket}
              onChange={(b) => {
                setBucket(b);
                setSelId(undefined);
              }}
            />
          </div>
          <div className="mt-3.5 flex flex-wrap items-center gap-3 px-[22px] pb-3.5">
            <input
              type="search"
              aria-label="поиск по очереди"
              placeholder="фамилия, файл или когорта"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelId(undefined);
              }}
              className="w-[260px] max-w-full rounded-sm border border-bd2 bg-bg2 px-[11px] py-[7px] font-mono text-[12.5px] text-ink outline-none placeholder:text-mut focus:border-acc"
            />
            <Segmented label="сортировка" value={sort} options={SORTS} onChange={setSort} testPrefix="sort" />
            <span className="flex-1" />
            <span className="font-mono text-[11.5px] text-mut" data-testid="shown-label">
              показано {rows.length} из {all.length}
            </span>
          </div>
          {all.length === 0 ? (
            <div className="px-[22px] pb-6">
              <EmptyState where="очередь проверки" title="В очереди пусто" body={EMPTY_BODY[mode]} marks={[true, true, false]} alt={mode === "mine" ? { label: "посмотреть всю школу", onClick: () => onMode("all") } : undefined} />
            </div>
          ) : (
            <>
              <Rows rows={rows} selectedId={selected?.submission_id} onSelect={setSelId} />
              {rows.length === 0 && (
                <div className="flex flex-col items-center gap-3 px-[22px] py-10" data-testid="filter-empty">
                  <span className="font-mono text-[12.5px] text-mut">по этому фильтру пусто</span>
                  <Button size="sm" onClick={reset}>
                    сбросить фильтр и поиск
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
        <aside className="flex flex-col gap-3.5 border-bd px-[18px] py-5 lg:border-l">
          <SelectedCard r={selected} showMentor={mode !== "mine"} />
          <HintsCard />
        </aside>
      </div>
    </Shell>
  );
}
